import React, { useEffect, useState } from 'react';
import { Box, CircularProgress, Typography, Paper } from '@mui/material';
import axiosInstance from '../../api/axios';
import { useAuth } from '../../context/AuthContext';

interface HydraChatProps {
  embedded?: boolean;
  width?: string | number;
  height?: string | number;
}

const HydraChat: React.FC<HydraChatProps> = ({ 
  embedded = true, 
  width = '100%', 
  height = '700px' 
}) => {
  const [chatUrl, setChatUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    const fetchChatUrl = async () => {
      if (!isAuthenticated) return;
      
      try {
        setLoading(true);
        
        const response = await axiosInstance.get(`/api/hydra-chat-url?embedded=${embedded}`);
        
        if (response.data && response.data.url) {
          setChatUrl(response.data.url);
        } else {
          setError('Failed to get chat URL from server');
        }
      } catch (err) {
        console.error('Error fetching chat URL:', err);
        setError('Error connecting to chat service. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchChatUrl();
  }, [isAuthenticated, embedded]);

  if (!isAuthenticated) {
    return (
      <Paper elevation={2} sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="body1">
          Please log in to access the chat.
        </Typography>
      </Paper>
    );
  }

  if (loading) {
    return (
      <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        height={height} 
        width={width}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Paper elevation={2} sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="body1" color="error">
          {error}
        </Typography>
      </Paper>
    );
  }

  if (!chatUrl) {
    return (
      <Paper elevation={2} sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="body1">
          Chat service unavailable. Please try again later.
        </Typography>
      </Paper>
    );
  }

  return (
    <Box 
      sx={{ 
        width: width, 
        height: height, 
        overflow: 'hidden',
        border: '1px solid #e0e0e0',
        borderRadius: 1,
        boxShadow: 1
      }}
    >
      <iframe
        src={chatUrl}
        title="Hydra Chat"
        width="100%"
        height="100%"
        style={{ 
          border: 'none',
          borderRadius: 'inherit'
        }}
        allow="camera; microphone"
      />
    </Box>
  );
};

export default HydraChat;